import React, { useMemo, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";

const parseMoney = (value) => {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0;
    if (Number.isInteger(value)) return value / 100;
    return value;
  }

  const s = String(value).trim();
  if (!s) return 0;

  if (/^-?\d+$/.test(s)) return Number(s) / 100;

  const normalized = s.replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Math.abs(Number(value || 0))
  );

const isPaidStatus = (status) => {
  const s = String(status || "").toLowerCase();
  return (
    s === "paid" ||
    s === "pago" ||
    s === "baixado" ||
    s === "settled" ||
    s === "received" ||
    s === "recebido"
  );
};

function classifyRisk(maxAtrasoDias) {
  const d = Number(maxAtrasoDias || 0);
  if (d >= 90) return "Crítico";
  if (d >= 60) return "Alto";
  if (d >= 30) return "Médio";
  return "Baixo";
}

function riskBadge(r) {
  const base = "inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs border";
  if (r === "Crítico" || r === "Alto") return `${base} border-error/30 bg-error/10 text-error`;
  if (r === "Médio") return `${base} border-warning/30 bg-warning/10 text-warning`;
  return `${base} border-success/30 bg-success/10 text-success`;
}

const OverdueClientsTable = ({ items = [], onCharge }) => {
  const [selected, setSelected] = useState([]);

  const rows = useMemo(() => {
    const list = Array.isArray(items) ? items : [];

    const atrasados = list.filter((i) => {
      const days = Number(i?.daysToDue);
      if (!Number.isFinite(days)) return false;
      if (isPaidStatus(i?.status)) return false;
      if (String(i?.status || "").toLowerCase() === "canceled") return false;
      return days < 0;
    });

    const map = new Map();

    atrasados.forEach((i) => {
      const customerName =
        String(i?.customer || i?.clientName || i?.cliente || "Não informado").trim() || "Não informado";
      const key = customerName.toLowerCase();

      if (!map.has(key)) {
        map.set(key, {
          id: key,
          customer: customerName,
          email: i?.email || "",
          phone: i?.phone || "",
          total: 0,
          qtd: 0,
          maxAtraso: 0,
          risco: "Baixo",
          ultimoContato: i?.ultimoContato || "-",
        });
      }

      const row = map.get(key);
      const v = parseMoney(i?.amount ?? i?.valor ?? 0);
      const atraso = Math.abs(Number(i?.daysToDue || 0));

      row.total += v;
      row.qtd += 1;
      row.maxAtraso = Math.max(row.maxAtraso, atraso);
      row.risco = classifyRisk(row.maxAtraso);
    });

    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [items]);

  const handleSelectAll = (checked) => {
    if (checked) setSelected(rows.map((r) => r.id));
    else setSelected([]);
  };

  const handleSelectOne = (id, checked) => {
    if (checked) setSelected((p) => [...p, id]);
    else setSelected((p) => p.filter((x) => x !== id));
  };

  return (
    <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="font-heading-semibold text-base text-card-foreground">Inadimplência e atrasos</h3>
          <p className="text-sm text-muted-foreground">
            {rows.length} cliente(s) com títulos vencidos no período
          </p>
        </div>

        {selected.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => onCharge?.(selected)}>
            <Icon name="Mail" size={16} />
            <span className="ml-2">Cobrar</span>
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[740px]">
          <thead className="bg-accent/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-2 text-left">
                <Checkbox
                  checked={rows.length > 0 && selected.length === rows.length}
                  indeterminate={selected.length > 0 && selected.length < rows.length}
                  onChange={handleSelectAll}
                />
              </th>

              <th className="px-4 py-2 text-left text-xs font-body-medium text-muted-foreground uppercase tracking-wider">
                Cliente
              </th>

              <th className="w-[140px] px-4 py-2 text-right text-xs font-body-medium text-muted-foreground uppercase tracking-wider">
                Valor vencido
              </th>

              <th className="w-[120px] px-4 py-2 text-center text-xs font-body-medium text-muted-foreground uppercase tracking-wider">
                Maior atraso
              </th>

              <th className="w-[110px] px-4 py-2 text-center text-xs font-body-medium text-muted-foreground uppercase tracking-wider">
                Risco
              </th>

              <th className="w-[130px] px-4 py-2 text-center text-xs font-body-medium text-muted-foreground uppercase tracking-wider">
                Último contato
              </th>

              <th className="w-[120px] px-4 py-2 text-right text-xs font-body-medium text-muted-foreground uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-accent/30 transition-colors">
                <td className="px-4 py-3 align-top">
                  <Checkbox checked={selected.includes(r.id)} onChange={(c) => handleSelectOne(r.id, c)} />
                </td>

                <td className="px-4 py-3 align-top">
                  <div className="text-sm text-card-foreground font-body-medium truncate max-w-[420px]">
                    {r.customer}
                  </div>
                  <div className="text-xs text-muted-foreground truncate max-w-[420px]">
                    {r.email || "-"} {r.phone ? `• ${r.phone}` : ""}
                  </div>
                  <div className="text-xs text-muted-foreground">{r.qtd} título(s)</div>
                </td>

                <td className="px-4 py-3 align-top text-right">
                  <div className="text-sm font-heading-semibold text-card-foreground whitespace-nowrap">
                    {formatCurrency(r.total)}
                  </div>
                </td>

                <td className="px-4 py-3 align-top text-center">
                  <span className="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs border border-border bg-background text-muted-foreground whitespace-nowrap">
                    {r.maxAtraso} dia{r.maxAtraso === 1 ? "" : "s"}
                  </span>
                </td>

                <td className="px-4 py-3 align-top text-center">
                  <span className={riskBadge(r.risco)}>{r.risco}</span>
                </td>

                <td className="px-4 py-3 align-top text-center">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">{r.ultimoContato}</span>
                </td>

                <td className="px-4 py-3 align-top text-right">
                  <Button variant="outline" size="xs" onClick={() => onCharge?.([r.id])}>
                    <Icon name="Mail" size={14} />
                    <span className="ml-2">Cobrar</span>
                  </Button>
                </td>
              </tr>
            ))}

            {rows.length === 0 && (
              <tr>
                <td className="px-6 py-10 text-center text-sm text-muted-foreground" colSpan={7}>
                  Sem clientes em atraso no período selecionado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OverdueClientsTable;
